import React, { useState, useEffect } from "react";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import { createChatSession } from "../../../services/agentService";
import { debug } from "../../../utils/debugLogger";

// ChatKit web component is declared in the @openai/chatkit-react package

interface ChatWindowProps {
  idToken: string; // User's auth token to fetch session
  onClose?: () => void;
  agentName?: string;
  workflowId?: string; // Optional workflow ID, defaults to Daisy AI workflow
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  idToken,
  onClose,
  agentName = "Daisy",
  workflowId,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionData, setSessionData] = useState<{
    clientSecret: string;
    workflowId: string;
  } | null>(null);

  // Conversation starters
  const starters = [
    {
      id: "onboarding-guide",
      icon: "book-open",
      label: "On-boarding Guide",
      caption: "Get help getting started with Daisy AI",
      suggestion:
        "I need help getting started with Daisy AI. Can you guide me through the on-boarding process?",
    },
    {
      id: "service-inquiry",
      icon: "user",
      label: "Service Inquiry",
      caption: "Ask questions about our services",
      suggestion: "I have a question about Daisy AI services.",
    },
  ];

  // Check if ChatKit is loaded and fetch chat session on mount
  useEffect(() => {
    const fetchSession = async () => {
      try {
        setIsLoadingSession(true);
        setError(null);

        // Debug: Check if ChatKit is available
        debug.log(
          "ChatKit available:",
          typeof window !== "undefined" && (window as any).ChatKit
        );
        debug.log(
          "Custom elements available:",
          typeof window !== "undefined" && (window as any).customElements
        );

        const session = await createChatSession(idToken, workflowId);

        // Session created successfully
        debug.log("Chat session created successfully:", session);

        setSessionData({
          clientSecret: session.client_secret,
          workflowId: session.workflow.id,
        });
      } catch (err) {
        debug.error("Failed to create chat session:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to connect to chat service"
        );
      } finally {
        setIsLoadingSession(false);
      }
    };

    if (idToken) {
      fetchSession();
    }
  }, [idToken, workflowId]);

  // Use the official ChatKit React hook
  const { control } = useChatKit({
    api: {
      async getClientSecret(existing) {
        if (existing) {
          return existing;
        }

        if (!sessionData?.clientSecret) {
          throw new Error("No client secret available");
        }

        return sessionData.clientSecret;
      },
    },
    onError: (detail) => {
      debug.error("ChatKit error:", detail);
      setError(detail.error?.message || "An error occurred with the chat");
    },
    onThreadChange: ({ threadId }) => {
      debug.log("Active thread ID changed:", threadId);
      // You can perform additional actions here based on the new threadId
      // For example: save threadId to state, send analytics, etc.
    },
    // Configure the start screen with greeting and conversation starters
    startScreen: {
      greeting: "How can I help you today?",
      prompts: starters.map((starter) => ({
        label: starter.label,
        prompt: starter.suggestion,
        icon: starter.icon as any, // Cast to ChatKitIcon type
      })),
    },
  });

  if (!idToken) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6">
          <p className="text-red-800 font-semibold">
            Authentication required to access chat
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
      <div className="bg-white shadow-2xl w-full max-w-xl h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-[#2f4941] text-white">
          <h2 className="text-lg font-medium tracking-wide font-sans">
            Chat with {agentName}
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
              aria-label="Close chat"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Chat Content */}
        <div className="flex-1 relative overflow-hidden">
          {isLoadingSession ? (
            // Loading state - Making secure connection
            <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
              </div>
              <p className="text-lg font-semibold text-gray-800 mb-2">
                Making secure connection...
              </p>
              <p className="text-sm text-gray-600">
                Connecting you to {agentName}
              </p>
            </div>
          ) : error ? (
            // Error state
            <div className="flex flex-col items-center justify-center h-full bg-red-50 p-6">
              <svg
                className="w-16 h-16 text-red-500 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-800 font-semibold text-lg mb-2">{error}</p>
              <p className="text-red-600 text-sm mb-4">
                Unable to establish connection
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Retry Connection
              </button>
            </div>
          ) : sessionData ? (
            // Chat interface with conversation starters
            <div className="h-full w-full">
              <ChatKit control={control} className="h-full w-full" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
